<template>
  <!--
    O CLÃ (D22, 7.4). Estado A — sem clã — é "a tela vende": o herói são OS TRÊS BÔNUS com o
    número que dão (a resposta a "por que eu entraria" antes de qualquer botão), os clãs abertos
    na própria tela com entrar grátis como ação primária, criar como secundária com a regra do
    tipo, e o ranking em faixa. Estado B é o cais do clã (ClanDetails). A cena é a do contrato do
    D8: recife-profundo — a âncora e o cardume.
  -->
  <FishScene cena="recife-profundo" :escurecimento="0.55" class="cla">
    <div class="cla__interior">
      <Loading v-if="loading" :message="$t('clan.loading')" />

      <template v-else>
        <!-- ==================== ESTADO A: sem clã ==================== -->
        <div v-if="!playerInClan" class="cla__porta">
          <div class="cla__topo">
            <h1 class="cla__titulo banner-titulo">{{ $t('clan.title') }}</h1>
          </div>

          <!-- O herói: o que um clã DÁ, em número (D22, problema 1). -->
          <section class="cla__heroi" :aria-label="$t('clan.boostsTab')">
            <article v-for="bonus in herois" :key="bonus.tipo" class="cla__promessa">
              <FishIcon :nome="bonus.icone" tamanho="grande" />
              <p class="cla__promessa-nome">{{ $t(`clan.boosts.${bonus.tipo}`) }}</p>
              <p class="cla__promessa-regra">{{ $t(`clan.regraBonus.${bonus.tipo}`) }}</p>
            </article>
          </section>

          <!-- Os clãs abertos, NA TELA (problema 1 do UI-18: os 60% vazios) — entrar é grátis. -->
          <section class="cla__abertos" :aria-label="$t('clan.joinClan')">
            <p class="cla__rotulo">
              {{ $t('clan.joinClan') }}
              <span class="cla__gratis">{{ $t('clan.entrarGratis') }}</span>
            </p>

            <p v-if="!openClans.length" class="cla__quieto">{{ $t('clan.noClansFound') }}</p>

            <ul v-else class="cla__lista">
              <li v-for="cla in openClans" :key="cla.id" class="cla__aberto">
                <FishIcon :nome="cla.type === 'corporation' ? 'lock' : 'globe'" tamanho="pequeno" />
                <div class="crescer">
                  <!-- O país ao lado do nome: é o que o jogador procura antes de entrar num clã. -->
                  <p class="cla__aberto-nome">
                    <BandeiraDePais v-if="cla.country" :codigo="cla.country" />
                    {{ cla.name }}
                  </p>
                  <p class="cla__aberto-meta">
                    {{ $t(`clan.types.${cla.type}`) }} ·
                    {{ cla.memberCount }} {{ $t('clan.members').toLowerCase() }} ·
                    <span class="t-numero">{{ formato(cla.pontuation) }}</span> pts
                  </p>
                </div>
                <FishButton
                  v-if="cla.type === 'cooperative'"
                  variante="primario"
                  tamanho="pequeno"
                  som="clique"
                  @click="handleJoinClan(cla.id)"
                >
                  {{ $t('clan.join') }}
                </FishButton>
                <FishButton v-else tamanho="pequeno" som="clique" @click="handleRequestJoin(cla.id)">
                  {{ $t('clan.requestJoin') }}
                </FishButton>
              </li>
            </ul>
          </section>

          <!-- Criar: secundária, com o requisito visível e a regra do tipo no modal. -->
          <section class="cla__criar">
            <div class="crescer">
              <p class="cla__rotulo">{{ $t('clan.createClan') }}</p>
              <ul class="cla__requisitos">
                <li :class="{ 'cla__requisito--ok': playerLevel >= 10 }">
                  <FishIcon nome="star" tamanho="miudo" />
                  {{ $t('clan.levelRequirement', { level: 10 }) }}
                </li>
                <li :class="{ 'cla__requisito--ok': playerGold >= 50000 }">
                  <FishIcon nome="coins" tamanho="miudo" />
                  {{ $t('clan.goldRequirement', { gold: '50.000' }) }}
                </li>
              </ul>
            </div>
            <FishButton
              tamanho="pequeno"
              :desabilitado="playerLevel < 10 || playerGold < 50000"
              :motivo="motivoDeCriar"
              som="clique"
              @click="showCreateClan = true"
            >
              {{ $t('clan.create') }}
            </FishButton>
          </section>

          <!-- O ranking em faixa: curiosidade, não ação (D22). -->
          <ClanRanking />
        </div>

        <!-- ==================== ESTADO B: o cais do clã ==================== -->
        <div v-else class="cla__dentro">
          <ClanDetails
            :clan="clanDetails"
            :is-leader="isPlayerClanLeader"
            @contribute="handleContribute"
            @leave="handleLeaveClan"
            @disband="handleDisbandClan"
            @kick="handleKickMember"
            @transfer="handleTransferLeadership"
            @upgradeBoost="handleUpgradeBoost"
            @activateBoost="handleActivateBoost"
            @chatError="handleChatError"
            @jogador-mudou="recarregarJogador"
          />
        </div>
      </template>

      <!-- Modals -->
      <CreateClanModal
        v-if="showCreateClan"
        @close="showCreateClan = false"
        @create="handleCreateClan"
      />

      <ConfirmModal
        v-if="showConfirmModal"
        :title="confirmConfig.title"
        :message="confirmConfig.message"
        :confirmText="confirmConfig.confirmText"
        :cancelText="confirmConfig.cancelText"
        @confirm="confirmConfig.onConfirm"
        @cancel="showConfirmModal = false"
      />

      <ContributeModal
        v-if="showContributeModal"
        :maxAmount="playerGold"
        @close="showContributeModal = false"
        @contribute="confirmContribute"
      />
    </div>
  </FishScene>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Loading from '@/shared/common/Loading.vue';
import ConfirmModal from '@/shared/common/ConfirmModal.vue';
import ClanDetails from './ClanDetails.vue';
import CreateClanModal from './CreateClanModal.vue';
import ContributeModal from './ContributeModal.vue';
import ClanRanking from './ClanRanking.vue';
import { FishScene, FishButton, FishIcon } from '@/shared/ui';
import BandeiraDePais from '@/shared/paises/BandeiraDePais.vue';

export default {
  name: 'ClanPage',
  components: {
    BandeiraDePais,
    Loading,
    ConfirmModal,
    ClanDetails,
    CreateClanModal,
    ContributeModal,
    ClanRanking,
    FishScene,
    FishButton,
    FishIcon
  },
  data() {
    return {
      showCreateClan: false,
      showConfirmModal: false,
      showContributeModal: false,
      confirmConfig: {
        title: '',
        message: '',
        confirmText: '',
        cancelText: '',
        onConfirm: null
      },
      contributeData: {
        clanId: null
      },
      // Os três bônus do herói, com o ícone de cada um (D20).
      herois: [
        { tipo: 'moreGold', icone: 'coins' },
        { tipo: 'moreExp', icone: 'graduation-cap' },
        { tipo: 'moreFishingPower', icone: 'atributo-poder' },
      ],
    };
  },
  computed: {
    ...mapState({
      loading: state => state.clan.loading,
      error: state => state.clan.error,
      openClans: state => state.clan.openClans,
      playerLevel: state => state.player.level,
      playerGold: state => state.player.gold
    }),
    ...mapGetters({
      playerInClan: 'clan/isPlayerInClan',
      isPlayerClanLeader: 'clan/isPlayerClanLeader',
      clanDetails: 'clan/clanDetails'
    }),
    motivoDeCriar() {
      if (this.playerLevel < 10) return this.$t('clan.levelRequirement', { level: 10 });
      if (this.playerGold < 50000) return this.$t('clan.goldRequirement', { gold: '50.000' });
      return '';
    },
  },
  methods: {
    ...mapActions({
      fetchPlayerClanDetails: 'clan/fetchPlayerClanDetails',
      fetchOpenClans: 'clan/fetchOpenClans',
      createClan: 'clan/createClan',
      joinClan: 'clan/joinClan',
      requestToJoinClan: 'clan/requestToJoinClan',
      leaveClan: 'clan/leaveClan',
      disbandClan: 'clan/disbandClan',
      contributeGold: 'clan/contributeGold',
      kickMember: 'clan/kickMember',
      transferLeadership: 'clan/transferLeadership',
      upgradeBoost: 'clan/upgradeBoost',
      activateBoost: 'clan/activateBoost'
    }),

    formato(n) {
      return new Intl.NumberFormat('pt-BR').format(n || 0);
    },

    /**
     * O BILHETE DE PAPEL, no lugar do `GameAlert` (09/09/2026).
     *
     * O antigo era a caixa preta arredondada com `backdrop-filter` que sobrou de
     * antes do redesenho — e no contribuir ela aparecia JUNTO com o bilhete, que
     * a store do clã já despacha desde a 1.1. Dois avisos para uma doação.
     *
     * A chave vai crua para o `FishAviso`: quem traduz é ele, na língua do
     * momento. Traduzir aqui congelaria a frase na língua de quando o clique
     * aconteceu — o mesmo defeito, só mais difícil de ver.
     */
    avisar(chave, parametros = {}, icone = '') {
      this.$store.dispatch('avisos/avisar', { chave, parametros, icone }, { root: true });
    },

    /**
     * O bilhete do que NÃO deu certo.
     *
     * A frase do servidor tem preferência sobre a nossa: é ela que diz "o caixa
     * não cobre" em vez de "falhou ao contribuir". A chave de reserva só entra
     * quando não veio nada de lá.
     */
    reclamar(erro, chaveDeReserva) {
      const texto = typeof erro === 'string' ? erro : erro?.message || '';
      this.$store.dispatch(
        'avisos/avisar',
        {
          ...(texto ? { texto } : { chave: chaveDeReserva }),
          tom: 'ruim',
          icone: 'exclamation-triangle',
        },
        { root: true },
      );
    },

    async handleCreateClan(clanData) {
      const result = await this.createClan(clanData);
      this.showCreateClan = false;

      if (result.success) {
        this.avisar('clan.createSuccess', {}, 'users');
        await this.fetchPlayerClanDetails();
      } else {
        this.reclamar(result.error, 'clan.createError');
      }
    },

    async handleJoinClan(clanId) {
      const result = await this.joinClan(clanId);

      if (result.success) {
        this.avisar('clan.joinSuccess', {}, 'users');
        await this.fetchPlayerClanDetails();
      } else {
        this.reclamar(result.error || result.message, 'clan.joinError');
      }
    },

    async handleRequestJoin(clanId) {
      const result = await this.requestToJoinClan(clanId);

      if (result.success) {
        this.avisar('clan.requestSuccess', {}, 'users');
      } else {
        this.reclamar(result.error, 'clan.requestError');
      }
    },

    handleLeaveClan() {
      this.confirmConfig = {
        title: this.$t('clan.leaveTitle'),
        message: this.$t('clan.leaveConfirm'),
        confirmText: this.$t('clan.leave'),
        cancelText: this.$t('common.cancel'),
        onConfirm: this.confirmLeaveClan
      };
      this.showConfirmModal = true;
    },

    async confirmLeaveClan() {
      this.showConfirmModal = false;
      const result = await this.leaveClan();

      if (result.success) {
        this.avisar('clan.leaveSuccess', {}, 'sign-out-alt');
      } else {
        this.reclamar(result.error, 'clan.leaveError');
      }
    },

    handleDisbandClan() {
      this.confirmConfig = {
        title: this.$t('clan.disbandTitle'),
        message: this.$t('clan.disbandConfirm'),
        confirmText: this.$t('clan.disband'),
        cancelText: this.$t('common.cancel'),
        onConfirm: this.confirmDisbandClan
      };
      this.showConfirmModal = true;
    },

    async confirmDisbandClan() {
      this.showConfirmModal = false;

      if (!this.clanDetails || !this.clanDetails.id) {
        this.reclamar(null, 'clan.noClanError');
        return;
      }

      const result = await this.disbandClan(this.clanDetails.id);

      if (result.success) {
        this.avisar('clan.disbandSuccess', {}, 'trash-alt');
      } else {
        this.reclamar(result.error, 'clan.disbandError');
      }
    },

    handleContribute() {
      if (!this.clanDetails || !this.clanDetails.id) {
        this.reclamar(null, 'clan.noClanError');
        return;
      }

      this.contributeData.clanId = this.clanDetails.id;
      this.showContributeModal = true;
    },

    async confirmContribute(amount) {
      this.showContributeModal = false;

      if (this.playerGold < amount) {
        this.reclamar(null, 'clan.notEnoughGold');
        return;
      }

      // Make sure we use the clan ID from the player's clan data
      const clanId = this.$store.state.player.clan?.id || this.contributeData.clanId;

      const result = await this.contributeGold({
        clanId,
        amount
      });

      // O bilhete do "contribuiu" é despachado pela store do clã, com as moedas
      // que o servidor creditou — repetir aqui era o aviso em dobro.
      if (!result.success) {
        this.reclamar(result.error, 'clan.contributeError');
      }
    },

    async handleKickMember({ clanId, playerId }) {
      this.confirmConfig = {
        title: this.$t('clan.kickTitle'),
        message: this.$t('clan.kickConfirm'),
        confirmText: this.$t('clan.kick'),
        cancelText: this.$t('common.cancel'),
        onConfirm: async () => {
          this.showConfirmModal = false;
          const result = await this.kickMember({ clanId, playerId });

          if (result.success) {
            this.avisar('clan.kickSuccess', {}, 'user-times');
          } else {
            this.reclamar(result.error, 'clan.kickError');
          }
        }
      };
      this.showConfirmModal = true;
    },

    async handleTransferLeadership({ clanId, newLeaderId }) {
      this.confirmConfig = {
        title: this.$t('clan.transferTitle'),
        message: this.$t('clan.transferConfirm'),
        confirmText: this.$t('clan.transfer'),
        cancelText: this.$t('common.cancel'),
        onConfirm: async () => {
          this.showConfirmModal = false;
          const result = await this.transferLeadership({ clanId, newLeaderId });

          if (result.success) {
            this.avisar('clan.transferSuccess', {}, 'crown');
          } else {
            this.reclamar(result.error, 'clan.transferError');
          }
        }
      };
      this.showConfirmModal = true;
    },

    async handleUpgradeBoost({ clanId, boostType }) {
      const result = await this.upgradeBoost({ clanId, boostType });

      if (result.success) {
        this.avisar(
          'clan.boostUpgradeSuccess',
          { type: this.$t(`clan.boosts.${boostType}`), level: result.newLevel },
          'star',
        );
      } else {
        this.reclamar(result.error, 'clan.boostUpgradeError');
      }
    },

    async handleActivateBoost({ clanId, boostType }) {
      const result = await this.activateBoost({ clanId, boostType });

      if (result.success) {
        this.avisar(
          'clan.boostActivateSuccess',
          { type: this.$t(`clan.boosts.${boostType}`) },
          'star',
        );
      } else {
        this.reclamar(result.error, 'clan.boostActivateError');
      }
    },

    handleChatError(erro) {
      this.reclamar(erro, 'clan.chatError');
    },

    /**
     * Depois de comprar na loja do clã.
     *
     * As moedas de clã ficam no HUD, e quem as guarda é a store do jogador — a
     * loja gastou-as no servidor e o número na barra continuaria o antigo até a
     * próxima navegação. Recarregar o perfil é uma ida, e é a que fecha o laço
     * entre a compra e o saldo que o jogador está olhando.
     */
    async recarregarJogador() {
      try {
        // A mesma action que a contribuição usa — ver `contributeGold` na store
        // do clã. Um segundo caminho para "recarregar o jogador" seria o começo
        // de dois estados diferentes para o mesmo saldo.
        await this.$store.dispatch('player/loadPlayerState', this.$store.state.player.id, {
          root: true,
        });
      } catch (error) {
        this.handleChatError(error);
      }
    }
  },
  async created() {
    await this.fetchPlayerClanDetails();
    if (!this.playerInClan) {
      await this.fetchOpenClans();
    }
  }
};
</script>

<style scoped>
.cla {
  min-height: 100vh;
}

.cla__interior {
  max-width: 980px;
  margin: 0 auto;
  /* O respiro de baixo conta a altura da barra de navegação — sem ele, as folhas da barra
   * cobriam o Enviar do mural. */
  padding: var(--e-5) var(--e-3) calc(var(--altura-barra, 104px) + var(--e-5));
}

/* O título no PERGAMINHO do Paper Pack (papel.css) — tinta sobre papel. */
.cla__topo {
  text-align: center;
  margin-bottom: var(--e-2);
}

.cla__titulo {
  font-family: var(--fonte-display);
  font-size: var(--f-5);
  line-height: var(--lh-curto);
  margin: 0;
}

@media (max-width: 599px) {
  .cla__titulo {
    font-size: var(--f-3);
  }
}

.cla__porta,
.cla__dentro {
  display: flex;
  flex-direction: column;
  gap: var(--e-4);
}

/* -------------------------------------------------------------------- herói */

.cla__heroi {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--e-3);
}

.cla__promessa {
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-panel.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background: var(--papel);
  background-clip: padding-box;
  image-rendering: pixelated;
  padding: var(--e-3);
  text-align: center;
  color: var(--tinta);
}

.cla__promessa-nome {
  margin: var(--e-2) 0 var(--e-1);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
}

.cla__promessa-regra {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

/* ------------------------------------------------------------ clãs abertos */

.cla__abertos,
.cla__criar {
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-panel.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background: var(--papel);
  background-clip: padding-box;
  image-rendering: pixelated;
  padding: var(--e-3);
  color: var(--tinta);
}

.cla__rotulo {
  margin: 0 0 var(--e-2);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta);
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  flex-wrap: wrap;
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

/* "Entrar é grátis" escrito na tela: o jogador de nível 3 lia "Nível 10" no card de CRIAR e
 * concluía que clã não era para ele (D22). */
.cla__gratis {
  font-family: var(--fonte-corpo);
  font-size: var(--f-1);
  color: var(--latao);
}

.cla__quieto {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  font-style: italic;
}

.cla__lista {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.cla__aberto {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding-bottom: var(--e-2);
  border-bottom: 1px dashed var(--papel-sombra);
}

.cla__aberto:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.cla__aberto-nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  display: flex;
  align-items: center;
  gap: var(--e-2);
}

.cla__aberto-meta {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

/* -------------------------------------------------------------------- criar */

.cla__criar {
  display: flex;
  align-items: center;
  gap: var(--e-3);
}

.cla__requisitos {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: var(--e-3);
  flex-wrap: wrap;
}

.cla__requisitos li {
  display: flex;
  align-items: center;
  gap: var(--e-1);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.cla__requisito--ok {
  color: var(--tinta);
}

@media (max-width: 599px) {
  .cla__criar {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
